import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Tooltip } from 'react-tooltip';
import { ChevronIcon, Copy, tooltipClasses } from 'configurator-ui-shared';

import { useConfiguratorDispatch, useRootSelector } from '../../../store';
import { ItemProperties } from '../../../types';
import {
  clearInvalidConfigIds,
  deleteConfig,
  duplicateConfig,
  selectActiveConfigId,
  selectAllConfigSnapshots,
  selectCanDeleteConfig,
  selectConfigIds,
  selectInvalidConfigIds,
  selectIsAtConfigCap,
  selectMaxConfigs,
  switchConfig
} from '../store/multiConfigSlice';
import { getConfigNameValue } from '../utils';

function getConfigLabel(properties: ItemProperties | undefined, index: number) {
  const fallback = `Name ${index + 1}`;
  const nameValue = getConfigNameValue(properties);
  if (!nameValue) return fallback;

  const [firstName, lastName] = nameValue.split('\n');
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || fallback;
}

export function ConfigSessionSwitcher() {
  const dispatch = useConfiguratorDispatch();
  const configIds = useRootSelector(selectConfigIds);
  const snapshots = useRootSelector(selectAllConfigSnapshots);
  const activeConfigId = useRootSelector(selectActiveConfigId);
  const canDelete = useRootSelector(selectCanDeleteConfig);
  const maxConfigs = useRootSelector(selectMaxConfigs);
  const isAtCap = useRootSelector(selectIsAtConfigCap);

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  // The dropdown list (and its tooltip) are portaled straight to <body> -
  // see the comment further down for why - so they're no longer DOM
  // descendants of containerRef. handleClickOutside needs its own ref to
  // that portaled content, otherwise clicking anything inside the open
  // dropdown (switch/duplicate/delete) would register as an "outside"
  // click and close it out from under the click that was meant to use it.
  const dropdownRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Since the dropdown is portaled to <body> instead of sitting right below
  // the toggle button via CSS, its position has to be computed manually
  // from the toggle's actual screen position - and kept in sync while
  // open, since the toggle can move under it (page scroll/resize) without
  // the portaled content moving on its own.
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const updateDropdownPosition = useCallback(() => {
    if (!toggleRef.current) return;
    const rect = toggleRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition();
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [isOpen, updateDropdownPosition]);

  // "Add Another Name"/duplicate both add a configuration without any other
  // visible change on screen (the base preview doesn't move) - whenever the
  // count goes up, pulse the dropdown toggle's border, and highlight the
  // newly added row. If the dropdown was closed when this happened (e.g. a
  // click on "Add Another Name", which lives outside it), briefly open it
  // so the highlight is actually visible, then close it again - if it was
  // already open (e.g. a click on a row's own duplicate button), leave it
  // open rather than yanking it shut on the shopper.
  const previousConfigCount = useRef(configIds.length);
  const [isPulsing, setIsPulsing] = useState(false);
  const [highlightedConfigId, setHighlightedConfigId] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (configIds.length > previousConfigCount.current) {
      const newConfigId = configIds[configIds.length - 1];
      const wasClosed = !isOpen;

      setIsPulsing(true);
      setHighlightedConfigId(newConfigId);
      if (wasClosed) setIsOpen(true);

      const pulseTimeout = setTimeout(() => setIsPulsing(false), 800);
      const highlightTimeout = setTimeout(() => {
        setHighlightedConfigId(null);
        if (wasClosed) setIsOpen(false);
      }, 1400);

      previousConfigCount.current = configIds.length;
      return () => {
        clearTimeout(pulseTimeout);
        clearTimeout(highlightTimeout);
      };
    }
    previousConfigCount.current = configIds.length;
    // isOpen is intentionally excluded - this should only react to the
    // configuration count changing, reading isOpen's value at that moment
    // rather than re-running whenever the shopper opens/closes it manually.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configIds]);

  // Add to Cart validation (useValidateMultiConfigNames) flags any
  // configuration missing a name here. Mirrors the "newly added" open/close
  // behavior above (briefly open if closed, leave open if already open),
  // but marks the offending rows with a red border instead of the blue
  // highlight, and clears itself the same way.
  const invalidConfigIds = useRootSelector(selectInvalidConfigIds);

  useEffect(() => {
    if (invalidConfigIds.length === 0) return;
    const wasClosed = !isOpen;
    if (wasClosed) setIsOpen(true);

    const timeout = setTimeout(() => {
      dispatch(clearInvalidConfigIds());
      if (wasClosed) setIsOpen(false);
    }, 1400);

    return () => clearTimeout(timeout);
    // isOpen is intentionally excluded - same reasoning as the effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidConfigIds, dispatch]);

  const countLabel =
    maxConfigs !== null
      ? `Names ${configIds.length}/${maxConfigs}`
      : `Names: ${configIds.length}`;

  // Below the switcher-worthy 2+ configurations, still surface the count
  // (e.g. "Names 1/12") - useful on its own once a product has a configured
  // minimum, so the shopper can see progress toward it before there's
  // anything to switch between.
  if (configIds.length <= 1) {
    return (
      <div
        className="text-dark-gray mb-2 mt-5 font-semibold"
        data-testid="config-count-indicator"
      >
        {countLabel}
      </div>
    );
  }

  const activeIndex = configIds.indexOf(activeConfigId ?? '');
  const activeSnapshot = snapshots.find(({ id }) => id === activeConfigId);

  return (
    <div
      className="relative mb-2"
      ref={containerRef}
      data-testid="config-session-switcher"
    >
      <div
        className="text-dark-gray mb-2 mt-5 font-semibold"
        data-testid="config-count-indicator"
      >
        {countLabel}
      </div>
      <button
        ref={toggleRef}
        type="button"
        className={clsx(
          // py-[11px] matches ContainedButton size="large" (BaseButton's
          // sizeClasses) exactly - py-2 (8px) looked close but was 3px
          // short on each side, making this toggle visibly shorter than
          // ConfigureAnotherButton despite both looking "the same" in code.
          'bg-secondary-button text-secondary-button-text flex w-full items-center justify-between gap-2 rounded-[50px] px-3 py-[11px]',
          isPulsing && 'animate-config-pulse'
        )}
        style={{ border: '1px solid currentColor' }}
        onClick={() => setIsOpen(open => !open)}
        data-testid="config-dropdown-toggle"
      >
        <span className="flex items-center gap-2">
          {activeSnapshot?.properties.style?.thumbnail_path && (
            <img
              src={activeSnapshot.properties.style.thumbnail_path}
              className="h-[24px] w-[24px] rounded object-scale-down object-center"
              alt=""
            />
          )}
          {getConfigLabel(activeSnapshot?.properties, activeIndex)}
        </span>
        <ChevronIcon
          width={'24px'}
          height={'24px'}
          direction={isOpen ? 'up' : 'down'}
        />
      </button>

      {isOpen &&
        dropdownPosition &&
        // The whole app renders inside a `relative z-[101]` root wrapper
        // (Configurator.tsx, added to clear a known third-party overlay
        // at z-index 100), so anything meant to visually float above the
        // rest of the page - like this dropdown, and react-tooltip, which
        // renders inline rather than portaling itself - has to escape
        // that stacking context entirely rather than out-z-index it
        // locally within a wrapper that's already behind other stuff.
        // Portaling both straight to <body> gets them out of that
        // context, but they still need their own z-index once there to
        // clear the app root itself - see the zIndex below. The
        // dropdown's position is computed from the toggle button's
        // actual screen coordinates since it can no longer rely on CSS
        // position:absolute relative to a parent it's no longer inside.
        createPortal(
          <>
            <Tooltip
              id="config-row-action-tooltip"
              className={tooltipClasses}
              style={{ zIndex: 151 }}
              delayShow={1000}
            />
            <ul
              ref={dropdownRef}
              className="overflow-hidden rounded-[20px] bg-white shadow"
              style={{
                position: 'fixed',
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                // 150 matches MobilePreviewWrapper's sticky header - the
                // one other place in this codebase that needed to float
                // above both the app root's z-[101] and a known
                // third-party review-widget loading overlay at z-index
                // 100. Portaling to <body> escapes the app root's
                // stacking context entirely, so matching that precedent
                // is the actual fix here, not avoiding z-index outright.
                zIndex: 150,
                border: '1px solid var(--nb-secondary-button-text, #014876)'
              }}
            >
              {snapshots.map(({ id: configId, properties }, index) => {
                const isActive = configId === activeConfigId;
                const hasName = !!getConfigNameValue(properties)?.trim();
                const isFirstRow = index === 0;
                const isLastRow = index === snapshots.length - 1;

                return (
                  <li
                    key={configId}
                    className={clsx(
                      // border-solid is required, not decorative - this
                      // project disables Tailwind's Preflight
                      // (corePlugins.preflight: false), so there's no
                      // global border-style:solid baseline. Per the CSS
                      // spec, border-width computes to 0 whenever
                      // border-style is (the default) none, regardless of
                      // what border-width says - every other border/
                      // border-2 usage in this codebase already pairs it
                      // with border-solid for this exact reason.
                      'flex items-center justify-between gap-2 border-2 border-solid px-3 py-2',
                      // The list itself is rounded-[20px] with
                      // overflow-hidden - without a matching radius here,
                      // a highlighted first/last row's square corners cut
                      // straight into that rounded mask instead of
                      // following its curve.
                      isFirstRow && 'rounded-t-[20px]',
                      isLastRow && 'rounded-b-[20px]',
                      isActive && 'bg-gray-100',
                      highlightedConfigId === configId &&
                        'animate-config-row-highlight',
                      // Computed live from this row's own current name
                      // value, same as the disabled-duplicate-button check
                      // below - not tied to invalidConfigIds (which only
                      // drives the auto-open-on-failed-click behavior now).
                      // Mutually exclusive, not layered - border-transparent
                      // and border-error both set border-color, and having
                      // both present at once left it up to Tailwind's
                      // generated stylesheet order (not this class list's
                      // order) to decide which won, which silently kept the
                      // border invisible.
                      hasName ? 'border-transparent' : 'border-error/60'
                    )}
                  >
                    <button
                      type="button"
                      className="flex flex-1 items-center gap-2 text-left"
                      onClick={() => {
                        dispatch(switchConfig(configId));
                        setIsOpen(false);
                      }}
                      data-testid="config-option-button"
                    >
                      <span
                        className="w-[22px] flex-shrink-0 text-left text-gray-500"
                        data-testid="config-option-index"
                      >
                        {index + 1}.
                      </span>
                      {properties.style?.thumbnail_path && (
                        <img
                          src={properties.style.thumbnail_path}
                          className="h-[24px] w-[24px] rounded object-scale-down object-center"
                          alt=""
                        />
                      )}
                      {getConfigLabel(properties, index)}
                    </button>
                    {!isAtCap && (
                      <button
                        type="button"
                        // h-6 w-6 (24px) matches the Plus/Minus accordion
                        // section icons' footprint, for visual consistency.
                        className="flex h-6 w-6 flex-shrink-0 items-center justify-center disabled:opacity-30"
                        aria-label={`Duplicate Name ${index + 1}`}
                        disabled={!hasName}
                        onClick={() => dispatch(duplicateConfig(configId))}
                        data-testid="config-option-duplicate-button"
                        data-tooltip-id="config-row-action-tooltip"
                        data-tooltip-content="Duplicate this name exactly"
                        data-tooltip-place="top"
                      >
                        <Copy />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        // h-6 w-6 (24px) matches the Plus/Minus accordion
                        // section icons' footprint, for visual consistency.
                        className="flex h-6 w-6 flex-shrink-0 items-center justify-center text-xl leading-none"
                        aria-label={`Delete Name ${index + 1}`}
                        onClick={() => dispatch(deleteConfig(configId))}
                        data-testid="config-option-delete-button"
                        data-tooltip-id="config-row-action-tooltip"
                        data-tooltip-content="Remove this name"
                        data-tooltip-place="top"
                      >
                        &times;
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </>,
          document.body
        )}
    </div>
  );
}
